import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SpotifyEmbed from "@/components/SpotifyEmbed";
import { LinkifiedText } from "@/components/LinkifiedText";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { buildFeedTitle, COLUMNAS_FEED_ITEM, getArticleDateLabel, getArticleSortTime, getSpotifyItemType, isVideoUrl, mergeFeedDescriptions, parseBlogImages, type ArticleRow, type FeedItemRow, type PlaylistRow } from "@/lib/contentModels";
import { devLogQueryPerf, readLocalQueryCache, writeLocalQueryCache } from "@/lib/localQueryCache";
import { thumbnailImageUrl } from "@/lib/imageCdn";
import { recuerdaPrimeraFoto } from "@/lib/primeraFoto";
import { atributosDeCarga } from "@/lib/imagePriority";
import { getSpotifyCanonicalUrl } from "@/lib/spotify";
import { cn } from "@/lib/utils";

const FALLBACK_IMAGE = "/logo-640.webp";
/**
 * Filas de feed_items que pide la portada. Pinta cinco posts y un post son
 * unas ocho filas y media de media, asi que con esto entran unos veinte:
 * margen de sobra aunque varios vengan sin foto y se descarten.
 */
const TOPE_PORTADA = 200;

type HomeSectionHeaderProps = {
  title: string;
  subtitle: string;
  to: string;
  linkLabel: string;
};
type SongOfDayRow = {
  id: string;
  title: string;
  description: string;
  spotify_url: string;
  published: boolean;
  created_at: string;
};

const HomeSectionHeader = ({ title, to, linkLabel }: HomeSectionHeaderProps) => (
  <header
    className={cn(
      "flex flex-wrap items-end justify-between gap-3 border-b-2",
      "mb-5 pb-4 md:mb-6 md:pb-5",
      "border-[#13151c]/28",
    )}
  >
    <h2
      className={cn(
        "font-display text-xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-2xl md:text-4xl",
        "text-[#13151c]",
      )}
    >
      {title}
    </h2>
    {linkLabel ? (
      <Link
        to={to}
        className={cn(
          "shrink-0 rounded-sm border px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.16em] shadow-[6px_6px_0_0_rgba(0,0,0,0.14)] transition md:px-5 md:py-2.5 md:text-[13px]",
          "border-[#13151c]/45 bg-[#13151c]/5 text-[#13151c] hover:-translate-y-0.5 hover:bg-[#13151c]/10 hover:border-[#13151c]/70",
        )}
      >
        {linkLabel}
      </Link>
    ) : null}
  </header>
);

const HomePlaylistCard = ({ post }: { post: PlaylistRow }) => {
  const spotifyLink = getSpotifyCanonicalUrl(post.spotify_url);

  return (
    <article className="border border-[#13151c]/15 bg-white p-4 transition hover:-translate-y-0.5 hover:border-primary/45 hover:shadow-[5px_5px_0_0_rgba(19,21,28,0.14)]">
      <div className="flex items-start gap-4">
        <img
          src={FALLBACK_IMAGE}
          alt=""
          className="h-14 w-14 shrink-0 rounded-full border border-[#13151c]/10 object-cover"
          loading="lazy"
        />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#13151c]/55">Playlist</p>
          <Link to={`/post/playlist/${post.id}`} className="mt-1 block">
            <h3 className="font-display text-xl font-bold leading-tight tracking-tight text-[#13151c] transition-colors hover:text-primary">
              {post.title}
            </h3>
          </Link>
          {post.description?.trim() ? (
            <p className="mt-2 line-clamp-3 font-mono text-[12px] leading-relaxed text-[#13151c]/72">
              {post.description}
            </p>
          ) : null}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link
          to={`/post/playlist/${post.id}`}
          className="inline-flex items-center rounded-sm border border-[#13151c]/35 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[#13151c] transition hover:-translate-y-0.5 hover:border-primary hover:text-primary"
        >
          Ver ficha
        </Link>
        {spotifyLink ? (
          <a
            href={spotifyLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-sm border border-[#13151c]/35 px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[#13151c] transition hover:-translate-y-0.5 hover:border-primary hover:text-primary"
          >
            Abrir en Spotify
          </a>
        ) : null}
      </div>
    </article>
  );
};

const Index = () => {
  const { isAdmin } = useAuth();
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  const {
    data: feed = [],
    isPending: isFeedPending,
    isFetching: isFeedFetching,
    isError: isFeedError,
    error: feedError,
  } = useQuery({
    /*
     * Clave propia, y no la del feed: compartirla obligaba a la portada a
     * traerse las 1173 filas de feed_items --dos peticiones, 1,09 MB, 900 ms
     * antes de poder pintar nada-- para enseñar cinco tarjetas. Y hasta que
     * no llegan esas filas no se sabe la URL de la primera foto, asi que
     * `eager` y `fetchPriority` no pueden hacer nada: no hay nada que pedir.
     *
     * Con TOPE_PORTADA entran unos veinte posts, de sobra para los cinco que
     * se pintan. Al ir a /feed, esa pagina pide la lista entera por su
     * cuenta, que es lo que necesita para buscar y ordenar.
     */
    queryKey: ["home-feed-items"],
    queryFn: async () => {
      const startedAt = performance.now();
      const feedTable = "feed_items" as never;
      const { data, error } = await supabase
        .from(feedTable)
        .select(COLUMNAS_FEED_ITEM)
        .eq("published", true)
        .order("fecha_utc", { ascending: false })
        .order("id", { ascending: false })
        .limit(TOPE_PORTADA);
      if (error) throw error;
      const rows = (data ?? []) as unknown as FeedItemRow[];
      devLogQueryPerf("home-feed-items", startedAt, rows.length);
      return rows;
    },
    initialData: () => readLocalQueryCache<FeedItemRow[]>("cache:home-feed-items"),
    initialDataUpdatedAt: 0,
    placeholderData: (previousData) => previousData,
    retry: 1,
  });

  const {
    data: blogs = [],
    isPending: isBlogsPending,
    isFetching: isBlogsFetching,
    isError: isBlogsError,
    error: blogsError,
  } = useQuery({
    queryKey: ["articulos-home"],
    queryFn: async () => {
      const startedAt = performance.now();
      const { data, error } = await supabase
        .from("articulos" as never)
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data ?? []) as unknown as ArticleRow[];
      const sorted = [...rows].sort((a, b) => getArticleSortTime(b) - getArticleSortTime(a)).slice(0, 3);
      devLogQueryPerf("home-articulos", startedAt, sorted.length);
      return sorted;
    },
    initialData: () => readLocalQueryCache<ArticleRow[]>("cache:articulos-home"),
    initialDataUpdatedAt: 0,
    placeholderData: (previousData) => previousData,
    retry: 1,
  });
  const { data: songOfDay = null, isPending: isSongPending, isFetching: isSongFetching } = useQuery({
    queryKey: ["song-of-day-home"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cancion_del_dia" as never)
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as SongOfDayRow | null;
    },
    initialData: () => readLocalQueryCache<SongOfDayRow | null>("cache:song-of-day-home"),
    initialDataUpdatedAt: 0,
    placeholderData: (previousData) => previousData,
    retry: 1,
  });
  const { data: featuredPlaylists = [], isPending: isPlaylistsPending, isFetching: isPlaylistsFetching } = useQuery({
    queryKey: ["home-featured-playlists"],
    queryFn: async () => {
      const startedAt = performance.now();
      const { data, error } = await supabase
        .from("playlists" as never)
        .select("*")
        .eq("published", true)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false })
        .limit(2);
      if (error) throw error;
      const rows = (data ?? []) as PlaylistRow[];
      devLogQueryPerf("home-featured-playlists", startedAt, rows.length);
      return rows;
    },
    initialData: () => readLocalQueryCache<PlaylistRow[]>("cache:home-featured-playlists"),
    initialDataUpdatedAt: 0,
    placeholderData: (previousData) => previousData,
    retry: 1,
  });

  useEffect(() => {
    writeLocalQueryCache("cache:home-feed-items", feed);
  }, [feed]);

  useEffect(() => {
    writeLocalQueryCache("cache:articulos-home", blogs);
  }, [blogs]);

  useEffect(() => {
    writeLocalQueryCache("cache:song-of-day-home", songOfDay);
  }, [songOfDay]);

  useEffect(() => {
    writeLocalQueryCache("cache:home-featured-playlists", featuredPlaylists);
  }, [featuredPlaylists]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktopLayout(mq.matches);
    update();
    // Safari < 14 no soporta addEventListener en MediaQueryList; fallback a addListener.
    if (typeof mq.addEventListener === "function") mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (typeof mq.removeEventListener === "function") mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  const photoPosts = useMemo(() => {
    const map = new Map<string, FeedItemRow[]>();
    for (const row of feed ?? []) {
      const list = map.get(row.post_id) ?? [];
      list.push(row);
      map.set(row.post_id, list);
    }

    const groups: Array<{ postId: string; urls: string[]; descripcion: string; fechaUtc: string | null; representativeRowId: number }> = [];
    for (const rows of map.values()) {
      rows.sort((a, b) => (a.item_index ?? 0) - (b.item_index ?? 0));
      let descripcion = "";
      for (const row of rows) {
        descripcion = mergeFeedDescriptions(descripcion, row.descripcion);
      }
      groups.push({
        postId: rows[0].post_id,
        urls: rows.map((row) => row.archivo).filter((url): url is string => Boolean(url)),
        descripcion: descripcion.trim(),
        fechaUtc: rows[0].fecha_utc ?? null,
        representativeRowId: rows[0].id,
      });
    }

    groups.sort((a, b) => {
      const ta = a.fechaUtc ? new Date(a.fechaUtc).getTime() : 0;
      const tb = b.fechaUtc ? new Date(b.fechaUtc).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return b.representativeRowId - a.representativeRowId;
    });

    return groups;
  }, [feed]);

  // La foto grande de Conciertos es el LCP de la portada: la apuntamos para que
  // la proxima visita la pida sin esperar al bundle (src/lib/primeraFoto.ts).
  const primeraPortada = photoPosts[0]?.urls[0];
  useEffect(() => {
    if (!primeraPortada || isVideoUrl(primeraPortada)) return;
    recuerdaPrimeraFoto(thumbnailImageUrl(primeraPortada));
  }, [primeraPortada]);

  const feedErrorText = feedError instanceof Error ? feedError.message : "No se pudo conectar con Supabase.";
  const blogsErrorText = blogsError instanceof Error ? blogsError.message : "No se pudo conectar con Supabase.";
  const shouldShowFeedSkeleton = isFeedPending || isFeedFetching;
  const shouldShowBlogsSkeleton = isBlogsPending || isBlogsFetching;
  const shouldShowSongSkeleton = isSongPending || isSongFetching;
  const shouldShowPlaylistsSkeleton = isPlaylistsPending || isPlaylistsFetching;

  const heroNavCls =
    "font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#121317]/92 underline decoration-[#121317]/45 underline-offset-[7px] transition hover:text-[#0b0c0e] hover:decoration-[#0b0c0e]/75 md:text-xs";

  return (
    <div className="min-w-0">
      <section
        id="home-hero"
        className="relative isolate mb-4 flex w-full flex-col overflow-hidden bg-[#f7f6f3] md:min-h-[28svh]"
        aria-labelledby="home-hero-title"
      >
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_68%_20%,rgba(17,18,20,0.08),transparent_62%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#1f2024]/35 to-transparent"
          aria-hidden
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[#1f2024]/55" aria-hidden />

        {/* ── Mobile hero: logo + title centrado, sin disco ── */}
        <div className="relative z-10 flex flex-col items-center px-5 py-8 md:hidden">
          <img
            src="/logo-256.webp"
            alt="Popistant"
            className="h-16 w-16 rounded-full border-2 border-[#13151c]/15 shadow-md"
            width={64}
            height={64}
          />
          <h1
            id="home-hero-title"
            className="mt-3 font-display text-[2rem] font-extrabold uppercase leading-none tracking-tight text-[#141519]"
          >
            Popistant
          </h1>
          <nav
            className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2"
            aria-label="Secciones principales"
          >
            <Link to="/feed" className={heroNavCls}>Conciertos</Link>
            <Link to="/playlists" className={heroNavCls}>Playlists</Link>
            <Link to="/articulos" className={heroNavCls}>Artículos</Link>
            <Link to="/contacto" className={heroNavCls}>Contacto</Link>
            {isAdmin ? <Link to="/admin" className={heroNavCls}>Admin</Link> : null}
          </nav>
        </div>

        {/* ── Desktop/tablet hero: disco + texto a la derecha ── */}
        <div className="relative z-10 hidden min-h-0 flex-1 flex-col px-9 pb-0 pt-1.5 md:flex">
          <div className="relative mx-auto flex min-h-0 w-full max-w-[80rem] flex-1 items-center">
            <div
              className="pointer-events-none absolute left-[8%] top-1/2 z-10 h-[clamp(10rem,30vw,34rem)] w-[clamp(10rem,30vw,34rem)] -translate-x-1/2 -translate-y-1/2 md:left-[12%] lg:left-[14%] xl:left-[15%]"
              aria-hidden
            >
              <div className="home-hero-vinyl-glow pointer-events-none absolute inset-[-8%] rounded-full bg-black/10 blur-[46px] md:blur-[56px] lg:blur-[66px]" />
              <div className="home-vinyl-disc-spin relative h-full w-full">
                <div className="home-hero-vinyl absolute inset-0 rounded-full" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative z-[1] aspect-square w-[36%] overflow-hidden rounded-full border-[2.5px] border-white/35 shadow-[inset_0_3px_18px_rgba(0,0,0,0.72)]">
                    <img src="/logo-640.webp" alt="" className="h-full w-full object-cover" width={320} height={320} />
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-20 ml-auto w-full max-w-[clamp(16rem,45vw,46rem)] text-right md:pr-2 lg:pr-4 xl:pr-6">
              <h1
                className="mt-3 flex flex-nowrap justify-end gap-x-0.5 whitespace-nowrap font-display text-[clamp(1.7rem,5.1vw,4.5rem)] font-extrabold uppercase leading-none tracking-tight text-[#141519]"
                aria-hidden
              >
                Popistant
              </h1>
              <nav
                className="mt-4 flex flex-wrap items-center justify-end gap-x-7 gap-y-2"
                aria-label="Secciones principales"
              >
                <Link to="/feed" className={heroNavCls}>Conciertos</Link>
                <Link to="/playlists" className={heroNavCls}>Playlists</Link>
                <Link to="/articulos" className={heroNavCls}>Artículos</Link>
                <Link to="/contacto" className={heroNavCls}>Contacto</Link>
                {isAdmin ? <Link to="/admin" className={heroNavCls}>Admin</Link> : null}
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Continuación editorial: portada modular tipo blog */}
      <div className="relative bg-[#f7f6f3] text-[#13151c]">
        <div className="pointer-events-none absolute inset-0 opacity-[0.018]" aria-hidden>
          <div
            className="h-full w-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative mx-auto max-w-[80rem] px-5 pb-14 pt-8 md:px-9 md:pb-16 md:pt-10">
          {isDesktopLayout ? (
            <div className="grid grid-cols-12 gap-7 lg:gap-10 lg:items-start">
              {/* Izquierda: Conciertos */}
              <section id="home-conciertos" className="scroll-mt-24 md:scroll-mt-28 lg:col-span-6">
                <HomeSectionHeader title="Conciertos" subtitle="" to="/feed" linkLabel="Ver todos" />
                {photoPosts.length > 0 ? (
                  <div className="grid gap-2.5 md:grid-cols-2">
                    {photoPosts.slice(0, 5).map((post, idx) => {
                      const cover = post.urls[0];
                      const fallbackCaption = buildFeedTitle({
                        id: 0,
                        post_id: post.postId,
                        shortcode: null,
                        post_type: null,
                        item_index: 0,
                        media_type: null,
                        fecha_utc: null,
                        descripcion: post.descripcion,
                        link: null,
                        archivo: cover ?? null,
                        published: true,
                      });
                      // Vista resumen con line-clamp-3: aplastamos saltos de párrafo a espacios
                      // para no perder líneas visibles en huecos vacíos.
                      const caption = ((post.descripcion ?? "").trim() || fallbackCaption).replace(/\s*\n\s*/g, " ");
                      const layoutClass =
                        idx === 0
                          ? "aspect-[4/5] md:col-span-2 md:aspect-[16/12.4]"
                          : "aspect-[4/5]";
                      return (
                        <Link
                          key={post.postId}
                          to={`/feed?view=scroll&post=${encodeURIComponent(post.postId)}`}
                          className={cn(
                            "group relative overflow-hidden border border-[#13151c]/18 bg-[#ffffff] transition",
                            "hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-[6px_6px_0_0_rgba(19,21,28,0.16)]",
                            layoutClass,
                          )}
                        >
                          {cover ? (
                            isVideoUrl(cover) ? (
                              <video src={cover} className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.04]" muted playsInline loop autoPlay preload="metadata" />
                            ) : (
                              <img
                                src={thumbnailImageUrl(cover)}
                                alt=""
                                {...atributosDeCarga(idx === 0)}
                                className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-[1.04]"
                              />
                            )
                          ) : (
                            <div className="flex h-full items-center justify-center px-3 text-center font-mono text-[11px] leading-snug text-[#13151c]/55">{caption}</div>
                          )}
                          {post.urls.length > 1 && (
                            <span className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center text-white" aria-label="Varias fotos o vídeos">
                              <svg viewBox="0 0 24 24" className="h-[19px] w-[19px]" fill="none" aria-hidden>
                                <rect x="8.8" y="6.8" width="9.4" height="9.4" rx="2" stroke="currentColor" strokeOpacity="0.92" strokeWidth="1.9" />
                                <rect x="5.8" y="3.8" width="9.4" height="9.4" rx="2" stroke="currentColor" strokeOpacity="0.75" strokeWidth="1.9" />
                              </svg>
                            </span>
                          )}
                          <div className={cn("absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/72 to-transparent", "p-3.5 pt-14")}>
                            <p className="font-mono text-[14px] leading-relaxed text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.85)] line-clamp-3">{caption}</p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : shouldShowFeedSkeleton ? (
                  <div className="grid gap-2.5 md:grid-cols-2">
                    <div className="md:col-span-2 aspect-[16/10] animate-pulse bg-[#13151c]/10" />
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="aspect-[5/4] animate-pulse bg-[#13151c]/10" />
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-[#13151c]/25 bg-[#13151c]/[0.03] px-6 py-12 text-center">
                    <p className="font-mono text-sm text-[#13151c]/60">
                      {isFeedError ? `Error cargando feed: ${feedErrorText}` : "Todavía no hay fotos en el archivo."}
                    </p>
                  </div>
                )}
              </section>

              {/* Derecha: Canción del día + Playlists (en el mismo bloque para que no se abra hueco) */}
              <div className="lg:col-span-6 flex flex-col gap-7 scroll-mt-24 md:scroll-mt-28">
                <section id="home-song-of-day">
                  <HomeSectionHeader title="Canción del día" subtitle="" to="/playlists" linkLabel="" />
                  <div className="grid gap-3">
                    {songOfDay ? (
                      <>
                        {songOfDay.description?.trim() ? (
                          <LinkifiedText text={songOfDay.description} className="font-mono text-[12px] leading-relaxed text-[#13151c]/75" />
                        ) : null}
                        <SpotifyEmbed url={songOfDay.spotify_url} compact />
                      </>
                    ) : shouldShowSongSkeleton ? (
                      <div className="h-36 animate-pulse bg-[#13151c]/10" />
                    ) : (
                      <p className="font-mono text-sm text-[#13151c]/60"> </p>
                    )}
                  </div>
                </section>

                <section id="home-playlists">
                  <HomeSectionHeader title="Playlists" subtitle="" to="/playlists" linkLabel="Ver todas" />
                  <div className="grid gap-3">
                    {featuredPlaylists.length > 0 ? (
                      featuredPlaylists
                        .filter((post) => Boolean(post.spotify_url))
                        .map((post) => {
                          const kind = getSpotifyItemType(post.spotify_url);
                          const isPlaylist = kind === "playlist";
                          // En móvil dejamos playlists más altas.
                          const height = isDesktopLayout ? (isPlaylist ? 420 : 152) : (isPlaylist ? 320 : 152);
                          return (
                            <SpotifyEmbed
                              key={post.id}
                              url={post.spotify_url}
                              height={height}
                              compact={!isPlaylist}
                              framed={isDesktopLayout}
                              deferUntilVisible
                            />
                          );
                        })
                    ) : shouldShowPlaylistsSkeleton ? (
                      Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-[320px] animate-pulse bg-[#13151c]/10" />)
                    ) : (
                      <p className="font-mono text-sm text-[#13151c]/60"> </p>
                    )}
                  </div>
                </section>
              </div>

              {/* Abajo: Artículos */}
              <section id="home-articulos" className="scroll-mt-24 md:scroll-mt-28 lg:col-span-12">
                <HomeSectionHeader title="Artículos" subtitle="" to="/articulos" linkLabel="Ver todos" />
                <div className="grid gap-3 pt-3 md:grid-cols-3">
                  {blogs.length > 0 ? (
                    blogs.map((post: ArticleRow) => (
                      <Link
                        key={post.post_num}
                        to={`/post/articulos/${post.post_num}`}
                        className="group border border-[#13151c]/15 bg-[#ffffff] p-2.5 transition hover:border-primary/45 hover:shadow-[5px_5px_0_0_rgba(19,21,28,0.14)]"
                      >
                        <div className="aspect-[16/10] overflow-hidden bg-[#13151c]/7">
                          {(() => {
                            const rawImage = parseBlogImages(post.imagenes)[0] ?? null;
                            const image = rawImage && !isVideoUrl(rawImage) ? rawImage : FALLBACK_IMAGE;
                            const isFallback = image === FALLBACK_IMAGE;
                            return (
                              <img
                                src={thumbnailImageUrl(image)}
                                alt=""
                                className={`h-full w-full object-cover transition duration-500 group-hover:scale-[1.04] ${isFallback ? "p-6 opacity-90" : ""}`}
                                loading="lazy"
                                onError={(event) => {
                                  event.currentTarget.onerror = null;
                                  event.currentTarget.src = FALLBACK_IMAGE;
                                  event.currentTarget.classList.add("p-6", "opacity-90");
                                }}
                              />
                            );
                          })()}
                        </div>
                        <div className="mt-2">
                          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-primary">{getArticleDateLabel(post)}</p>
                          <h3 className="mt-1 line-clamp-3 font-display text-xl font-bold leading-tight text-[#13151c]">{post.titulo}</h3>
                        </div>
                      </Link>
                    ))
                  ) : shouldShowBlogsSkeleton ? (
                    Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 animate-pulse bg-[#13151c]/10" />)
                  ) : (
                    <div className="md:col-span-3 border border-dashed border-[#13151c]/25 px-5 py-10 text-center">
                      <p className="font-mono text-sm text-[#13151c]/60">
                        {isBlogsError ? `Error cargando artículos: ${blogsErrorText}` : "Pronto habrá piezas en esta columna."}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          ) : (
            <div className="flex flex-col gap-10">
              {/* 1. Canción del día */}
              <section id="home-song-of-day" className="scroll-mt-24 md:scroll-mt-28">
                <HomeSectionHeader title="Canción del día" subtitle="" to="/playlists" linkLabel="" />
                <div className="grid gap-3">
                  {songOfDay ? (
                    <>
                      {songOfDay.description?.trim() ? (
                        <LinkifiedText text={songOfDay.description} className="font-mono text-[12px] leading-relaxed text-[#13151c]/75" />
                      ) : null}
                      <SpotifyEmbed url={songOfDay.spotify_url} compact />
                    </>
                  ) : shouldShowSongSkeleton ? (
                    <div className="h-36 animate-pulse bg-[#13151c]/10" />
                  ) : (
                    <p className="font-mono text-sm text-[#13151c]/60"> </p>
                  )}
                </div>
              </section>

              {/* 2. Conciertos */}
              <section id="home-conciertos" className="scroll-mt-24 md:scroll-mt-28">
                <HomeSectionHeader title="Conciertos" subtitle="" to="/feed" linkLabel="Ver todos" />
                {photoPosts.length > 0 ? (
                  <div className="grid gap-2.5 md:grid-cols-2">
                    {photoPosts.slice(0, 5).map((post, idx) => {
                      const cover = post.urls[0];
                      const fallbackCaption = buildFeedTitle({
                        id: 0,
                        post_id: post.postId,
                        shortcode: null,
                        post_type: null,
                        item_index: 0,
                        media_type: null,
                        fecha_utc: null,
                        descripcion: post.descripcion,
                        link: null,
                        archivo: cover ?? null,
                        published: true,
                      });
                      // Vista resumen con line-clamp-3: aplastamos saltos de párrafo a espacios
                      // para no perder líneas visibles en huecos vacíos.
                      const caption = ((post.descripcion ?? "").trim() || fallbackCaption).replace(/\s*\n\s*/g, " ");
                      const layoutClass =
                        idx === 0
                          ? "aspect-[4/5] md:col-span-2 md:aspect-[16/12.4]"
                          : "aspect-[4/5]";
                      return (
                        <Link
                          key={post.postId}
                          to={`/feed?view=scroll&post=${encodeURIComponent(post.postId)}`}
                          className={cn(
                            "group relative overflow-hidden border border-[#13151c]/18 bg-[#ffffff] transition",
                            "hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-[6px_6px_0_0_rgba(19,21,28,0.16)]",
                            layoutClass,
                          )}
                        >
                          {cover ? (
                            isVideoUrl(cover) ? (
                              <video src={cover} className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.04]" muted playsInline loop autoPlay preload="metadata" />
                            ) : (
                              <img src={thumbnailImageUrl(cover)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-[1.04]" />
                            )
                          ) : (
                            <div className="flex h-full items-center justify-center px-3 text-center font-mono text-[11px] leading-snug text-[#13151c]/55">{caption}</div>
                          )}
                          {post.urls.length > 1 && (
                            <span className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center text-white" aria-label="Varias fotos o vídeos">
                              <svg viewBox="0 0 24 24" className="h-[19px] w-[19px]" fill="none" aria-hidden>
                                <rect x="8.8" y="6.8" width="9.4" height="9.4" rx="2" stroke="currentColor" strokeOpacity="0.92" strokeWidth="1.9" />
                                <rect x="5.8" y="3.8" width="9.4" height="9.4" rx="2" stroke="currentColor" strokeOpacity="0.75" strokeWidth="1.9" />
                              </svg>
                            </span>
                          )}
                          <div className={cn("absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/72 to-transparent", "p-3.5 pt-14")}>
                            <p className="font-mono text-[14px] leading-relaxed text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.85)] line-clamp-3">{caption}</p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ) : shouldShowFeedSkeleton ? (
                  <div className="grid gap-2.5 md:grid-cols-2">
                    <div className="md:col-span-2 aspect-[16/10] animate-pulse bg-[#13151c]/10" />
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="aspect-[5/4] animate-pulse bg-[#13151c]/10" />
                    ))}
                  </div>
                ) : (
                  <div className="border border-dashed border-[#13151c]/25 bg-[#13151c]/[0.03] px-6 py-12 text-center">
                    <p className="font-mono text-sm text-[#13151c]/60">
                      {isFeedError ? `Error cargando feed: ${feedErrorText}` : "Todavía no hay fotos en el archivo."}
                    </p>
                  </div>
                )}
              </section>

              {/* 3. Playlists */}
              <section id="home-playlists" className="scroll-mt-24 md:scroll-mt-28">
                <HomeSectionHeader title="Playlists" subtitle="" to="/playlists" linkLabel="Ver todas" />
                <div className="grid gap-3">
                  {featuredPlaylists.length > 0 ? (
                    featuredPlaylists
                      .filter((post) => Boolean(post.spotify_url))
                      .map((post) => (
                        <SpotifyEmbed
                          key={post.id}
                          url={post.spotify_url}
                          height={152}
                          framed={false}
                          deferUntilVisible
                        />
                      ))
                  ) : shouldShowPlaylistsSkeleton ? (
                    Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-[152px] animate-pulse bg-[#13151c]/10" />)
                  ) : (
                    <p className="font-mono text-sm text-[#13151c]/60"> </p>
                  )}
                </div>
              </section>

              {/* 4. Artículos */}
              <section id="home-articulos" className="scroll-mt-24 md:scroll-mt-28">
                <HomeSectionHeader title="Artículos" subtitle="" to="/articulos" linkLabel="Ver todos" />
                <div className="grid gap-3 pt-3 md:grid-cols-3">
                  {blogs.length > 0 ? (
                    blogs.map((post: ArticleRow) => (
                      <Link
                        key={post.post_num}
                        to={`/post/articulos/${post.post_num}`}
                        className="group border border-[#13151c]/15 bg-[#ffffff] p-2.5 transition hover:border-primary/45 hover:shadow-[5px_5px_0_0_rgba(19,21,28,0.14)]"
                      >
                        <div className="aspect-[16/10] overflow-hidden bg-[#13151c]/7">
                          {(() => {
                            const rawImage = parseBlogImages(post.imagenes)[0] ?? null;
                            const image = rawImage && !isVideoUrl(rawImage) ? rawImage : FALLBACK_IMAGE;
                            const isFallback = image === FALLBACK_IMAGE;
                            return (
                              <img
                                src={thumbnailImageUrl(image)}
                                alt=""
                                className={`h-full w-full object-cover transition duration-500 group-hover:scale-[1.04] ${isFallback ? "p-6 opacity-90" : ""}`}
                                loading="lazy"
                                onError={(event) => {
                                  event.currentTarget.onerror = null;
                                  event.currentTarget.src = FALLBACK_IMAGE;
                                  event.currentTarget.classList.add("p-6", "opacity-90");
                                }}
                              />
                            );
                          })()}
                        </div>
                        <div className="mt-2">
                          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-primary">{getArticleDateLabel(post)}</p>
                          <h3 className="mt-1 line-clamp-3 font-display text-xl font-bold leading-tight text-[#13151c]">{post.titulo}</h3>
                        </div>
                      </Link>
                    ))
                  ) : shouldShowBlogsSkeleton ? (
                    Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 animate-pulse bg-[#13151c]/10" />)
                  ) : (
                    <div className="md:col-span-3 border border-dashed border-[#13151c]/25 px-5 py-10 text-center">
                      <p className="font-mono text-sm text-[#13151c]/60">
                        {isBlogsError ? `Error cargando artículos: ${blogsErrorText}` : "Pronto habrá piezas en esta columna."}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Index;
